// frontend/src/services/career.service.ts

import { supabase } from '../lib/supabaseClient';
import { authService } from './auth.service';
import { CareerPath, GenerateCareerPathResponse } from '../types/career.types';

const API_BASE = (import.meta.env.PROD && (import.meta.env.VITE_API_BASE_URL === 'http://localhost:8080' || !import.meta.env.VITE_API_BASE_URL)) ? '' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080');

class CareerApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CareerApiError';
  }
}

export const DEFAULT_CAREERS: CareerPath[] = [
  { id: 'c-game-dev', title: 'Oyun Geliştirici', description: 'Eğlenceli 2D/3D oyunlar tasarlayıp kodlayan uzman.', icon: '🎮', required_skills: ['yazilim', 'oyun-tasarimi', 'matematik'] },
  { id: 'c-web-dev', title: 'Web & Yazılım Geliştirici', description: 'İnternet sitelerini ve karmaşık sistemleri kodlayan geliştirici.', icon: '🌐', required_skills: ['html', 'css', 'javascript'] },
  { id: 'c-ai-engineer', title: 'Yapay Zeka & Veri Mühendisi', description: 'Akıllı sistemler, yapay zeka ve robotlar tasarlayan mühendis.', icon: '🤖', required_skills: ['yazilim', 'matematik', 'veri-bilimi'] },
  { id: 'c-mobile-dev', title: 'Mobil Uygulama Geliştirici', description: 'Telefon ve tablet uygulamaları geliştiren uzman.', icon: '📱', required_skills: ['yazilim', 'mobil', 'ui-design'] },
  { id: 'c-math-analyst', title: 'Matematikçi & Veri Analisti', description: 'Sayılar, formüller ve veri analiziyle dünyadaki sorunları çözen uzman.', icon: '📐', required_skills: ['matematik', 'mantik', 'veri-analizi'] },
  { id: 'c-space-scientist', title: 'Uzay & Bilim İnsanı', description: 'Gezegenleri, yıldızları ve evrenin sırlarını inceleyen araştırmacı.', icon: '🚀', required_skills: ['fen-bilimleri', 'astronomi', 'fizik'] },
  { id: 'c-robotics-eng', title: 'Robotik & Otomasyon Uzmanı', description: 'Akıllı robotlar, sensörler ve elektronik devreler üreten mühendis.', icon: '🦾', required_skills: ['robotik', 'elektronik', 'kodlama'] },
  { id: 'c-digital-artist', title: 'Dijital Sanatçı & Grafik Tasarımcı', description: 'Çizim, animasyon ve dijital karakterler üreten sanatçı.', icon: '🎨', required_skills: ['resim', 'dijital-sanat', 'animasyon'] },
  { id: 'c-language-expert', title: 'Uluslararası İletişim & Dil Uzmanı', description: 'Farklı diller öğrenip dünya çapında iletişim kuran dil uzmanı.', icon: '🌍', required_skills: ['ingilizce', 'yabanci-dil', 'iletisim'] },
  { id: 'c-eco-scientist', title: 'Çevre Bilimci & Ekoloji Uzmanı', description: 'Doğayı, canlıları ve gezegenimizin geleceğini koruyan bilim insanı.', icon: '🌿', required_skills: ['biyoloji', 'cevre-bilimi', 'doga'] },
  { id: 'c-music-producer', title: 'Dijital Müzik & Ses Tasarımcısı', description: 'Dijital sesler, besteler ve oyun müzikleri üreten müzisyen.', icon: '🎵', required_skills: ['muzik', 'ses-tasarimi', 'teknoloji'] },
  { id: 'c-cyber-sec', title: 'Siber Güvenlik Uzmanı', description: 'Dijital sistemleri ve verileri siber tehditlere karşı koruyan kalkan.', icon: '🛡️', required_skills: ['siber-guvenlik', 'mantik', 'aglar'] },
];

export const careerService = {
  async listCareerPaths(): Promise<CareerPath[]> {
    try {
      const { data, error } = await supabase
        .from('career_paths')
        .select('id, title, description, icon, required_skills')
        .order('title', { ascending: true });

      if (error || !data || data.length === 0) {
        return DEFAULT_CAREERS;
      }

      const existingTitles = new Set(data.map((d) => d.title.trim().toLowerCase()));
      const missingDefaults = DEFAULT_CAREERS.filter((c) => !existingTitles.has(c.title.trim().toLowerCase()));

      return [...data, ...missingDefaults];
    } catch {
      return DEFAULT_CAREERS;
    }
  },

  /**
   * Öğrencinin daha önce bu kariyer için üretilmiş bir yol haritası var mı
   * kontrol eder (learning_paths tablosunda title eşleşmesiyle — basit ama
   * yeterli bir bağ, çünkü career.routes.ts da aynı title'ı kullanıyor).
   */
  async getSavedPath(careerTitle: string, studentId: string) {
    const { data: path, error: pathError } = await supabase
      .from('learning_paths')
      .select('id, title')
      .eq('student_id', studentId)
      .eq('title', careerTitle)
      .maybeSingle();

    if (pathError) throw pathError;
    if (!path) return null;

    const { data: steps, error: stepsError } = await supabase
      .from('learning_path_steps')
      .select('title, is_completed, order_index')
      .eq('learning_path_id', path.id)
      .order('order_index', { ascending: true });

    if (stepsError) throw stepsError;

    return { pathId: path.id, steps: steps ?? [] };
  },

  async generatePath(careerPathId: string): Promise<GenerateCareerPathResponse> {
    const token = await authService.getAccessToken();
    if (!token) throw new CareerApiError('Oturum bulunamadı, lütfen giriş yapın.');

    const res = await fetch(`${API_BASE}/api/career/${careerPathId}/generate`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });

    const data = await res.json();
    if (!res.ok) throw new CareerApiError(data?.error?.message || 'Kariyer yolu oluşturulamadı.');
    return data;
  },

  async getMotivation(careerPathId: string): Promise<string> {
    const token = await authService.getAccessToken();
    if (!token) throw new CareerApiError('Oturum bulunamadı, lütfen giriş yapın.');

    const res = await fetch(`${API_BASE}/api/career/${careerPathId}/motivation`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok) throw new CareerApiError(data?.error?.message || 'Mesaj alınamadı.');
    return data.message;
  },
};

export { CareerApiError };
